import { useState } from 'react'
import { fn, supabase } from '../auth/supabase'

/**
 * Email, plus a team code the first time.
 *
 * The code is asked for only when the backend says it does not recognise the
 * address, so a returning player types their email and nothing else. That is not
 * cosmetic: the code guards account creation alone, which is what lets it be
 * rotated without disturbing anyone who already has an account (03.2).
 */
export function SignIn({ onBack }: { onBack(): void }) {
  const [email, setEmail] = useState('')
  const [teamCode, setTeamCode] = useState('')
  const [needsCode, setNeedsCode] = useState(false)
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [code, setCode] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    const address = email.trim().toLowerCase()
    if (!address) {
      setError('Enter your email address.')
      return
    }
    const url = fn('auth-start')
    if (!url) {
      setError('This build has no backend configured.')
      return
    }

    setBusy(true)
    setError(null)
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: address, teamCode: needsCode ? teamCode.trim() : undefined }),
      })
      const body = (await res.json().catch(() => ({}))) as {
        ready?: boolean
        needsCode?: boolean
        error?: string
      }

      if (body.needsCode) {
        setNeedsCode(true)
        setError(null)
        return
      }
      if (!res.ok) {
        setError(body.error ?? 'Could not send the code. Try again.')
        return
      }
      if (!body.ready || !supabase) {
        setError('Could not start sign-in. Try again.')
        return
      }

      /*
       * This sends the email carrying the code. No emailRedirectTo: there is no
       * link in the email and no redirect back to the app (03.5), so there is
       * nowhere for it to point. shouldCreateUser is false because auth-start has
       * already decided whether an account may exist.
       */
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: address,
        options: { shouldCreateUser: false },
      })
      if (otpError) {
        setError(`Could not send the code: ${otpError.message}`)
        return
      }
      setSent(true)
    } catch {
      setError('No connection. Try again when you are back online.')
    } finally {
      setBusy(false)
    }
  }

  /*
   * The code path, which is the one that actually works on an installed iOS PWA.
   *
   * Sign-in is by code, not by link (03.5). verifyOtp exchanges the code for a
   * session right here, in the context that asked for it, with no redirect --
   * which is what made a magic link fail on an installed iOS PWA, where the link
   * opened in Safari and the session never reached the app.
   *
   * The code is NOT assumed to be six digits. Supabase's OTP length is a
   * per-project setting (Authentication -> Email -> OTP length), and this one is
   * 8. Hard-coding six meant an eight-digit code could not be entered at all.
   * The field takes 6-10 and submits whatever is there; the server is the only
   * thing that actually knows the right length, so let it be the judge.
   */
  const verify = async () => {
    const address = email.trim().toLowerCase()
    const token = code.replace(/\D/g, '')
    if (token.length < 6) {
      setError('Enter the code from the email.')
      return
    }
    if (!supabase) {
      setError('This build has no backend configured.')
      return
    }
    setVerifying(true)
    setError(null)
    try {
      const { error: otpError } = await supabase.auth.verifyOtp({ email: address, token, type: 'email' })
      if (otpError) {
        setError(
          /expired/i.test(otpError.message)
            ? 'That code has expired. Send a new one.'
            : 'That code did not match. Check the digits and try again.',
        )
        return
      }
      // Success: onAuthStateChange in AuthProvider picks up the new session and
      // moves the app on, so there is nothing more to do here.
    } catch {
      setError('No connection. Try again when you are back online.')
    } finally {
      setVerifying(false)
    }
  }

  if (sent) {
    return (
      <div className="landing">
        <div className="landing__card">
          <div className="landing__label">Check your email</div>
          <div className="landing__opponent">Enter your code</div>
          <p>
            We emailed <strong>{email.trim().toLowerCase()}</strong> a code. Enter it here to finish
            signing in.
          </p>

          <label className="field">
            <span>Code from the email</span>
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              autoCorrect="off"
              // 6-10 digits: the length is a per-project Supabase setting, so the
              // field must not assume one. See the engineering rules.
              maxLength={10}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 10))}
              placeholder="Enter the code"
              // The one field the scorer must get right at courtside; make it big.
              style={{ fontSize: 28, letterSpacing: '0.3em', textAlign: 'center' }}
            />
          </label>

          {error && <div className="manage__note manage__note--error">{error}</div>}

          <button
            type="button"
            className="btn btn--primary btn--start"
            disabled={verifying || code.length < 6}
            onClick={() => void verify()}
          >
            {verifying ? 'Checking...' : 'Sign in'}
          </button>

          <p className="muted small">
            Nothing after a minute? Check your spam folder. The address it comes from is new, so
            some inboxes filter the first one.
          </p>
          <button type="button" className="btn" onClick={() => { setSent(false); setCode(''); setError(null) }}>
            Use a different address
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="landing">
      <div className="landing__card">
        <div className="landing__label">Sign in</div>

        <label className="field">
          <span>Email</span>
          <input
            type="email"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </label>

        {needsCode && (
          <>
            <label className="field">
              <span>Team code</span>
              <input
                value={teamCode}
                autoCapitalize="characters"
                autoCorrect="off"
                onChange={(e) => setTeamCode(e.target.value)}
                placeholder="Ask in the team chat"
              />
            </label>
            <p className="muted small">
              We do not recognise that address, so this looks like a first sign-in. The code is only
              needed once.
            </p>
          </>
        )}

        {error && <div className="manage__note manage__note--error">{error}</div>}

        <button type="button" className="btn btn--primary btn--start" disabled={busy} onClick={() => void submit()}>
          {busy ? 'Sending...' : needsCode ? 'Create my account' : 'Email me a code'}
        </button>

        <button type="button" className="btn" onClick={onBack}>
          Back
        </button>
      </div>
    </div>
  )
}
